import React, { useRef, useState } from 'react'
import { useWeb3React } from '@web3-react/core'
import { ChainId } from '@uniswap/sdk'
import { ChevronDown, ChevronUp } from 'react-feather'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { useOnClickOutside } from '../../hooks/useOnClickOutside'
import { switchToKChain, switchToBSC } from '../../utils/networkSwitcher'
import KLogo from '../../assets/images/k-logo.png'
import BNBLogo from '../../assets/images/bnb-logo.png'
import Loader from '../Loader'

const StyledMenu = styled.div`
  margin-left: 0.5rem;
  margin-right: 0.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  border: none;
  text-align: left;

  .desktop-only {
    display: inline;
  }

  .mobile-only {
    display: none;
  }

  .mobile-short {
    display: none;
  }

  ${({ theme }) => theme.mediaWidth.upToExtraSmall`
    margin-left: 0;
    margin-right: 0.3rem;

    .desktop-only {
      display: none;
    }

    .mobile-only {
      display: inline;
    }
  `};

  ${({ theme }) => theme.mediaWidth.upTo460`
    .mobile-only {
      display: none;
    }

    .mobile-short {
      display: inline;
    }
  `};
`

const StyledMenuButton = styled.button<{ isOpen?: boolean }>`
  position: relative;
  width: auto;
  height: 100%;
  border: none;
  margin: 0;
  padding: 0.5rem 0.65rem;
  min-height: 40px;
  background-color: ${({ theme, isOpen }) => (isOpen ? 'rgba(44, 47, 54, 0.95)' : theme.bg2)};
  border-radius: ${({ isOpen }) => (isOpen ? '16px 16px 0 0' : '12px')};
  border: ${({ isOpen }) => (isOpen ? '1px solid rgba(255, 255, 255, 0.1)' : 'none')};
  border-bottom: ${({ isOpen }) => (isOpen ? 'none' : 'none')};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  overflow: hidden;
  cursor: pointer;
  font-size: 15px;
  font-weight: 500;
  color: ${({ theme }) => theme.text1};
  white-space: nowrap;

  ${({ theme }) => theme.mediaWidth.upTo500`
    padding: 0.4rem 0.5rem;
    min-height: 36px;
    font-size: 13px;
    gap: 4px;
  `};

  :hover,
  :focus {
    cursor: pointer;
    outline: none;
    background-color: ${({ theme, isOpen }) => (isOpen ? 'rgba(44, 47, 54, 0.95)' : theme.bg3)};
  }

  :disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    margin-top: 2px;
  }
`

const MenuFlyout = styled.span`
  min-width: 240px;
  background-color: rgba(44, 47, 54, 0.95);
  backdrop-filter: blur(10px);
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.3);

  border: 1px solid rgba(255, 255, 255, 0.1);
  border-top: none;

  border-radius: 0 0 16px 16px;
  display: flex;
  flex-direction: column;
  font-size: 1rem;
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 100;
  overflow: hidden;
  padding: 0.5rem 0 0.75rem 0;
  animation: slideDown 0.2s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  ${({ theme }) => theme.mediaWidth.upTo500`
    min-width: 180px;
  `};
`

const MenuItem = styled.div`
  padding: 0.85rem 1rem;
  color: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  justify-content: flex-start;
  white-space: nowrap;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 400;
  transition: all 0.15s ease-in-out;

  :hover {
    color: rgba(255, 255, 255, 0.9);
    background-color: rgba(255, 255, 255, 0.05);
  }
`

const NetworkLogo = styled.img<{ size?: string }>`
  width: ${({ size }) => size || '18px'};
  height: ${({ size }) => size || '18px'};
  border-radius: 50%;
  margin-right: ${({ size }) => (size === '20px' ? '10px' : '0')};
  flex-shrink: 0;
  box-shadow: 0px 6px 10px rgba(0, 0, 0, 0.075);
`

export default function NetworkSelector() {
  const { chainId } = useWeb3React()
  const [isOpen, setIsOpen] = useState(false)
  const [isSwitching, setIsSwitching] = useState(false)
  const node = useRef<HTMLDivElement>()
  const { t } = useTranslation()

  useOnClickOutside(node, isOpen ? () => setIsOpen(false) : undefined)

  const handleNetworkSwitch = async (targetChainId: ChainId) => {
    setIsSwitching(true)
    try {
      let result: 'already' | 'switched' | 'failed'

      if (targetChainId === ChainId.K) {
        result = await switchToKChain()
      } else if (targetChainId === ChainId.BSC) {
        result = await switchToBSC()
      } else {
        result = 'failed'
      }

      if (result === 'switched') {
        setIsOpen(false)
        // 网络切换成功，短暂等待后刷新页面，提供更好的视觉反馈
        setTimeout(() => {
          window.location.reload()
        }, 1000)
      } else if (result === 'already') {
        setIsOpen(false)
        setIsSwitching(false)
      } else {
        console.error('Failed to switch network')
        setIsSwitching(false)
      }
    } catch (error) {
      console.error('Network switch error:', error)
      setIsSwitching(false)
    }
  }

  const getCurrentNetworkName = () => {
    if (!chainId) return t('switching')
    if (chainId === ChainId.K) return 'K Chain'
    if (chainId === ChainId.BSC) return 'BNB Smart Chain'
    return 'Select Network' // Fallback for other chains
  }

  const getCurrentNetworkNameMobile = () => {
    if (!chainId) return t('switching')
    if (chainId === ChainId.K) return 'K Chain'
    if (chainId === ChainId.BSC) return 'BNB Smart Chain'
    return 'Select Network' // Fallback for other chains
  }

  const getCurrentNetworkNameMobileShort = () => {
    if (!chainId) return t('switching')
    if (chainId === ChainId.K) return 'K Chain'
    if (chainId === ChainId.BSC) return 'BSC'
    return 'Select' // Fallback for other chains
  }

  const getNetworkLogo = (targetChainId: ChainId, size?: string) => {
    if (targetChainId === ChainId.BSC) {
      return <NetworkLogo src={BNBLogo} alt="BNB" size={size} />
    } else {
      return <NetworkLogo src={KLogo} alt="K" size={size} />
    }
  }

  return (
    <StyledMenu ref={node as any}>
      <StyledMenuButton isOpen={isOpen} onClick={() => setIsOpen(!isOpen)} disabled={isSwitching}>
        {chainId ? getNetworkLogo(chainId as ChainId) : <NetworkLogo src={KLogo} alt="Default" />}
        <span style={{ whiteSpace: 'nowrap' }}>
          <span className="desktop-only">{getCurrentNetworkName()}</span>
          <span className="mobile-only">{getCurrentNetworkNameMobile()}</span>
          <span className="mobile-short">{getCurrentNetworkNameMobileShort()}</span>
        </span>
        {isSwitching && <Loader />}
        {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </StyledMenuButton>

      {isOpen && (
        <MenuFlyout>
          <MenuItem onClick={() => handleNetworkSwitch(ChainId.K)}>{getNetworkLogo(ChainId.K, '20px')}K Chain</MenuItem>
          <MenuItem onClick={() => handleNetworkSwitch(ChainId.BSC)}>
            {getNetworkLogo(ChainId.BSC, '20px')}
            BNB Smart Chain
          </MenuItem>
        </MenuFlyout>
      )}
    </StyledMenu>
  )
}
